import React, { useState, useEffect, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Textarea } from './ui/textarea'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog'
import { Badge } from './ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table'
import {
  PlusIcon, PencilIcon, TrashIcon,
  PauseIcon, PlayIcon, ArchiveBoxIcon, ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline'
import { toast } from 'react-hot-toast'
import { revenueApi, companiesApi, customersApi } from '../services/api'
import { useTableSort } from '../hooks/useTableSort'

// Leistungskategorien — gleiche Liste wie beim Import (docs/EINNAHMEN-PFLEGE.md)
const CATEGORIES = [
  'wartung', 'hosting', 'domain', 'email', 'seo', 'sea', 'social',
  '360-hosting', 'zeitkontingent', 'projekt', 'sonstiges',
]

const INTERVAL_LABELS = {
  monthly: 'monatlich',
  quarterly: 'vierteljährlich',
  semi_annually: 'halbjährlich',
  annually: 'jährlich',
}

const TYPE_LABELS = {
  planned: 'Geplant',
  one_time: 'Einmalig',
  recurring: 'Laufend',
}

const STATUS_LABELS = {
  planned: 'Geplant',
  confirmed: 'Bestätigt',
  invoiced: 'Abgerechnet',
  paid: 'Bezahlt',
  overdue: 'Überfällig',
  cancelled: 'Storniert',
  paused: 'Pausiert',
  archived: 'Archiviert',
}

// Pausiert/Archiviert zählen weder in Summen noch im Cashflow mit
const INACTIVE_STATUSES = ['paused', 'archived']

const EMPTY_FORM = {
  title: '',
  description: '',
  amount: '',
  revenue_type: 'recurring',
  status: 'confirmed',
  category: '',
  expected_date: new Date().toISOString().split('T')[0],
  companyId: '',
  customer_id: '',
  customer_name: '',
  recurring_interval: 'monthly',
  recurring_end_date: '',
}

const RevenueManager = () => {
  const [revenues, setRevenues] = useState([])
  const [summary, setSummary] = useState({})
  const [companies, setCompanies] = useState([])
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingRevenue, setEditingRevenue] = useState(null)
  const [formData, setFormData] = useState(EMPTY_FORM)
  const [typeFilter, setTypeFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [brandFilter, setBrandFilter] = useState('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [viewTab, setViewTab] = useState('aktiv')

  useEffect(() => {
    loadRevenues()
    loadSummary()
    companiesApi.getAll().then((r) => setCompanies(r.data || [])).catch(() => {})
    customersApi.getAll('', 500).then((r) => setCustomers(r.data || [])).catch(() => {})
  }, [])

  const loadRevenues = async () => {
    try {
      setLoading(true)
      const response = await revenueApi.getAll('')
      setRevenues(response.data || [])
    } catch (error) {
      console.error('Error loading revenues:', error)
      toast.error('Fehler beim Laden der Einnahmen')
    } finally {
      setLoading(false)
    }
  }

  const loadSummary = async () => {
    try {
      const response = await revenueApi.getSummary('')
      setSummary(response.data || {})
    } catch (error) {
      console.error('Error loading revenue summary:', error)
    }
  }

  const companyName = (id) => companies.find((c) => c.id === id)?.name || '—'

  const tabOf = (r) =>
    r.status === 'paused' ? 'pausiert' : r.status === 'archived' ? 'archiv' : 'aktiv'

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return revenues.filter((r) => {
      if (tabOf(r) !== viewTab) return false
      if (typeFilter !== 'all' && r.revenue_type !== typeFilter) return false
      if (statusFilter !== 'all' && r.status !== statusFilter) return false
      if (brandFilter !== 'all' && r.company_id !== brandFilter) return false
      if (categoryFilter !== 'all' && r.category !== categoryFilter) return false
      if (q && !(`${r.title} ${r.customer_name || ''} ${r.category || ''} ${companyName(r.company_id)}`.toLowerCase().includes(q))) return false
      return true
    })
  }, [revenues, viewTab, typeFilter, statusFilter, brandFilter, categoryFilter, search, companies])

  // Schnell-Status wie bei den Fixkosten: ⏸ pausieren, ▶ fortsetzen, 🗄 archivieren, ↩ reaktivieren
  const quickStatus = async (revenue, status) => {
    try {
      await revenueApi.update(revenue.id, { status })
      toast.success(
        status === 'paused' ? 'Einnahme pausiert' :
        status === 'archived' ? 'Einnahme archiviert' : 'Einnahme wieder aktiv'
      )
      await loadRevenues()
      await loadSummary()
    } catch (error) {
      console.error('Error updating status:', error)
      toast.error('Status konnte nicht geändert werden')
    }
  }

  const { sorted, requestSort, indicator } = useTableSort(filtered, 'expected_date')

  // KPI-Kacheln folgen der Filterung: nur die gefilterten Zeilen zählen.
  // Laufende Einnahmen werden auf Monats-Äquivalent normiert.
  const filteredSummary = useMemo(() => {
    const monthlyEq = filtered
      .filter(r => r.revenue_type === 'recurring')
      .reduce((s, r) => {
        const iv = r.recurring_interval
        const f = iv === 'annually' ? 1 / 12 : iv === 'semi_annually' ? 1 / 6 : iv === 'quarterly' ? 1 / 3 : 1
        return s + (Number(r.amount) || 0) * f
      }, 0)
    const confirmed = filtered.filter(r => r.status === 'confirmed').reduce((s, r) => s + (Number(r.amount) || 0), 0)
    const planned = filtered.filter(r => r.status === 'planned').reduce((s, r) => s + (Number(r.amount) || 0), 0)
    return { monthly: monthlyEq, confirmed, planned, count: filtered.length }
  }, [filtered])

  // Monats-Gegenwert einer Position (fürs Sortieren nach "€/Monat" gedanklich):
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.title || !formData.amount || !formData.expected_date) {
      toast.error('Bitte Titel, Betrag und Datum ausfüllen')
      return
    }
    try {
      setLoading(true)
      const customer = customers.find((c) => c.id === formData.customer_id)
      const payload = {
        title: formData.title,
        description: formData.description || null,
        amount: parseFloat(formData.amount),
        revenue_type: formData.revenue_type,
        status: formData.status,
        category: formData.category || null,
        expected_date: formData.expected_date,
        companyId: formData.companyId || null,
        customer_id: formData.customer_id || null,
        customer_name: customer ? (customer.company || customer.name) : (formData.customer_name || null),
        recurring_interval: formData.revenue_type === 'recurring' ? (formData.recurring_interval || 'monthly') : null,
        recurring_end_date: formData.recurring_end_date || null,
      }
      if (editingRevenue) {
        // Update-Route kennt companyId nicht als Feld — Brand bleibt, Rest aktualisieren
        const { companyId, ...updatePayload } = payload
        await revenueApi.update(editingRevenue.id, updatePayload)
        toast.success('Einnahme aktualisiert')
      } else {
        await revenueApi.create(payload)
        toast.success('Einnahme angelegt')
      }
      resetForm()
      await loadRevenues()
      await loadSummary()
    } catch (error) {
      console.error('Error saving revenue:', error)
      toast.error('Fehler beim Speichern der Einnahme')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (revenue) => {
    setEditingRevenue(revenue)
    setFormData({
      title: revenue.title || '',
      description: revenue.description || '',
      amount: String(revenue.amount ?? ''),
      revenue_type: revenue.revenue_type || 'recurring',
      status: revenue.status || 'confirmed',
      category: revenue.category || '',
      expected_date: revenue.expected_date ? String(revenue.expected_date).split('T')[0] : new Date().toISOString().split('T')[0],
      companyId: revenue.company_id || '',
      customer_id: revenue.customer_id || '',
      customer_name: revenue.customer_name || '',
      recurring_interval: revenue.recurring_interval || 'monthly',
      recurring_end_date: revenue.recurring_end_date ? String(revenue.recurring_end_date).split('T')[0] : '',
    })
    setShowForm(true)
  }

  const handleDelete = async (revenueId) => {
    if (!window.confirm('Diese Einnahme wirklich löschen?')) return
    try {
      setLoading(true)
      await revenueApi.delete(revenueId)
      toast.success('Einnahme gelöscht')
      await loadRevenues()
      await loadSummary()
    } catch (error) {
      console.error('Error deleting revenue:', error)
      toast.error('Fehler beim Löschen der Einnahme')
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setFormData(EMPTY_FORM)
    setEditingRevenue(null)
    setShowForm(false)
  }

  const getStatusColor = (status) => {
    const colors = {
      planned: 'bg-slate-100 text-slate-700',
      confirmed: 'bg-amber-100 text-amber-800',
      invoiced: 'bg-slate-200 text-slate-800',
      paid: 'bg-emerald-100 text-emerald-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-slate-100 text-slate-500',
      paused: 'bg-yellow-100 text-yellow-800',
      archived: 'bg-slate-200 text-slate-500',
    }
    return colors[status] || 'bg-slate-100 text-slate-700'
  }

  const fmtMoney = (v) =>
    (v || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

  const sortableHead = (label, key, extra = '') => (
    <TableHead
      className={`cursor-pointer select-none hover:bg-slate-50 ${extra}`}
      onClick={() => requestSort(key)}
    >
      {label}{indicator(key)}
    </TableHead>
  )

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-mono font-bold tracking-tight text-slate-800">
              €{fmtMoney(filteredSummary.monthly)}
            </div>
            <div className="text-sm text-slate-600">Laufend / Monat (netto)</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-mono font-bold tracking-tight text-amber-600">
              €{fmtMoney(filteredSummary.confirmed)}
            </div>
            <div className="text-sm text-slate-600">Bestätigt (Pipeline)</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-mono font-bold tracking-tight text-slate-800">
              €{fmtMoney(filteredSummary.planned)}
            </div>
            <div className="text-sm text-slate-600">Geplant</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-mono font-bold tracking-tight text-slate-800">
              {filteredSummary.count}
            </div>
            <div className="text-sm text-slate-600">Einträge gesamt</div>
          </CardContent>
        </Card>
      </div>

      {/* Aktionen + Filter */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <Button
          onClick={() => { setEditingRevenue(null); setFormData(EMPTY_FORM); setShowForm(true) }}
          className="bg-amber-500 hover:bg-amber-600 text-white"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Neue Einnahme
        </Button>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex bg-slate-100 rounded-lg p-1">
            {[['aktiv', 'Aktiv'], ['pausiert', 'Pausiert'], ['archiv', 'Archiv']].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setViewTab(key)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                  viewTab === key ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {label} ({revenues.filter((r) => tabOf(r) === key).length})
              </button>
            ))}
          </div>
          <Input
            placeholder="Suchen (Titel, Kunde, Kategorie)…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-56"
          />
          <Select value={brandFilter} onValueChange={setBrandFilter}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Brands</SelectItem>
              {companies.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Kategorien</SelectItem>
              {CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Typen</SelectItem>
              <SelectItem value="recurring">Laufend</SelectItem>
              <SelectItem value="one_time">Einmalig</SelectItem>
              <SelectItem value="planned">Geplant</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              {Object.entries(STATUS_LABELS).map(([v, l]) => (
                <SelectItem key={v} value={v}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Formular */}
      {/* Bearbeiten/Anlegen im Popup statt inline — Formular war sonst weit
          unter der Tabelle und wirkte wie ein Seitenwechsel */}
      <Dialog open={showForm} onOpenChange={(o) => { if (!o) resetForm() }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {editingRevenue ? 'Einnahme bearbeiten' : 'Neue Einnahme anlegen'}
            </DialogTitle>
          </DialogHeader>
          <div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Titel *</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="z.B. Wartung example.de"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Betrag netto (€) *</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Kunde</label>
                  <Select
                    value={formData.customer_id || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, customer_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Kein Kunde —</SelectItem>
                      {[...customers]
                        .sort((a, b) => (a.company || a.name || '').localeCompare(b.company || b.name || '', 'de'))
                        .map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.company || c.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Brand</label>
                  <Select
                    value={formData.companyId || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, companyId: v === 'none' ? '' : v })}
                    disabled={!!editingRevenue}
                  >
                    <SelectTrigger><SelectValue placeholder="Brand wählen" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Standard-Brand</SelectItem>
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Kategorie</label>
                  <Select
                    value={formData.category || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, category: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Kategorie" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Keine —</SelectItem>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Typ</label>
                  <Select
                    value={formData.revenue_type}
                    onValueChange={(v) => setFormData({ ...formData, revenue_type: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="recurring">Laufend (wiederkehrend)</SelectItem>
                      <SelectItem value="one_time">Einmalig</SelectItem>
                      <SelectItem value="planned">Geplant</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.revenue_type === 'recurring' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Zyklus</label>
                    <Select
                      value={formData.recurring_interval}
                      onValueChange={(v) => setFormData({ ...formData, recurring_interval: v })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(INTERVAL_LABELS).map(([v, l]) => (
                          <SelectItem key={v} value={v}>{l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Status</label>
                  <Select
                    value={formData.status}
                    onValueChange={(v) => setFormData({ ...formData, status: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_LABELS).map(([v, l]) => (
                        <SelectItem key={v} value={v}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {formData.revenue_type === 'recurring' ? 'Startdatum *' : 'Datum *'}
                  </label>
                  <Input
                    type="date"
                    value={formData.expected_date}
                    onChange={(e) => setFormData({ ...formData, expected_date: e.target.value })}
                    required
                  />
                </div>
                {formData.revenue_type === 'recurring' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Enddatum (optional)</label>
                    <Input
                      type="date"
                      value={formData.recurring_end_date}
                      onChange={(e) => setFormData({ ...formData, recurring_end_date: e.target.value })}
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Beschreibung</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Zusätzliche Informationen…"
                  rows={2}
                />
              </div>

              <div className="flex justify-end space-x-3">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Abbrechen
                </Button>
                <Button type="submit" disabled={loading} className="bg-amber-500 hover:bg-amber-600 text-white">
                  {loading ? 'Speichern…' : editingRevenue ? 'Aktualisieren' : 'Anlegen'}
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tabelle */}
      <Card>
        <CardHeader>
          <CardTitle>
            Einnahmen-Übersicht{' '}
            <span className="text-sm font-normal text-slate-500">
              ({sorted.length} von {revenues.length})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading && revenues.length === 0 ? (
            <div className="text-center py-8 text-slate-600">Lade Einnahmen…</div>
          ) : sorted.length === 0 ? (
            <div className="text-center py-8 text-slate-600">Keine Einnahmen gefunden</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {sortableHead('Titel', 'title')}
                    {sortableHead('Kunde', 'customer_name')}
                    {sortableHead('Kategorie', 'category')}
                    {sortableHead('Typ', 'revenue_type')}
                    {sortableHead('Zyklus', 'recurring_interval')}
                    {sortableHead('Betrag (netto)', 'amount', 'text-right')}
                    {sortableHead('Datum', 'expected_date')}
                    {sortableHead('Status', 'status')}
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map((revenue) => (
                    <TableRow key={revenue.id} className="hover:bg-slate-50">
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => handleEdit(revenue)}
                          title="Bearbeiten"
                          className="text-left rounded-md px-2 py-1 -mx-2 hover:bg-amber-50 border border-transparent hover:border-amber-300 transition-colors"
                        >
                          <div className="font-medium text-slate-800 hover:text-amber-700">{revenue.title}</div>
                          {revenue.description && (
                            <div className="text-xs text-slate-400 max-w-[280px] truncate">{revenue.description}</div>
                          )}
                        </button>
                      </TableCell>
                      <TableCell className="text-sm text-slate-700">{revenue.customer_name || '—'}</TableCell>
                      <TableCell className="text-sm text-slate-600">{revenue.category || '—'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {TYPE_LABELS[revenue.revenue_type] || revenue.revenue_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {revenue.recurring_interval ? INTERVAL_LABELS[revenue.recurring_interval] : '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono font-bold tracking-tight text-slate-800">
                        €{fmtMoney(revenue.amount)}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {revenue.expected_date
                          ? new Date(revenue.expected_date).toLocaleDateString('de-DE')
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(revenue.status)}>
                          {STATUS_LABELS[revenue.status] || revenue.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end space-x-1">
                          {!INACTIVE_STATUSES.includes(revenue.status) ? (
                            <>
                              <Button
                                size="sm" variant="ghost" title="Pausieren"
                                onClick={() => quickStatus(revenue, 'paused')}
                                className="text-slate-500 hover:text-yellow-600"
                              >
                                <PauseIcon className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm" variant="ghost" title="Archivieren"
                                onClick={() => quickStatus(revenue, 'archived')}
                                className="text-slate-500 hover:text-slate-700"
                              >
                                <ArchiveBoxIcon className="h-4 w-4" />
                              </Button>
                            </>
                          ) : revenue.status === 'paused' ? (
                            <>
                              <Button
                                size="sm" variant="ghost" title="Fortsetzen"
                                onClick={() => quickStatus(revenue, 'confirmed')}
                                className="text-slate-500 hover:text-emerald-600"
                              >
                                <PlayIcon className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm" variant="ghost" title="Archivieren"
                                onClick={() => quickStatus(revenue, 'archived')}
                                className="text-slate-500 hover:text-slate-700"
                              >
                                <ArchiveBoxIcon className="h-4 w-4" />
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="sm" variant="ghost" title="Reaktivieren"
                              onClick={() => quickStatus(revenue, 'confirmed')}
                              className="text-slate-500 hover:text-emerald-600"
                            >
                              <ArrowUturnLeftIcon className="h-4 w-4" />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" title="Bearbeiten" onClick={() => handleEdit(revenue)}>
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Löschen"
                            onClick={() => handleDelete(revenue.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default RevenueManager
